/**
 * Possession chains — the unit analysts actually think in (founder ask,
 * 2026-08-27).
 *
 * Nobody watches a match frame by frame. They watch it possession by
 * possession: "this one, fourteen passes, ended in a box entry". The pipeline
 * already produces exactly that (analytics/possession_chains.parquet, 166 for
 * this game) with pass counts, zones, an xG and a box-entry flag. This turns
 * them into something navigable.
 */

export interface Chain {
  chain_id: number
  team_id: number
  start_frame: number
  end_frame: number
  pass_count: number
  /** null: the xG is withheld until the match is reviewed — never 0. */
  chain_xg: number | null
  start_zone: string | null
  end_zone: string | null
  box_entry: boolean
  /** Where possession was won and lost, in metres, centre-origin. Optional
   *  because older pipeline outputs predate them. */
  start_x?: number
  start_y?: number
  end_x?: number
  end_y?: number
  /** Per chain, not per team: ends swap at half-time. */
  attacking_direction?: 'left' | 'right'
  /** JSON string of jersey numbers, as stored. */
  player_sequence?: unknown
}

/** A finite number, or undefined — null and NaN both mean "not recorded". */
const num = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined

/** Parse the endpoint envelope defensively; drop anything unusable. */
export function parseChains(payload: unknown): Chain[] {
  const p = payload as { chains?: unknown[] } | null
  const list = (p?.chains ?? (Array.isArray(payload) ? payload : [])) as unknown[]
  const out: Chain[] = []
  for (const r of list) {
    if (!r || typeof r !== 'object') continue
    const c = r as Partial<Chain>
    if (!Number.isFinite(c.start_frame) || !Number.isFinite(c.end_frame)) continue
    if ((c.end_frame as number) < (c.start_frame as number)) continue
    out.push({
      chain_id: (c.chain_id as number) ?? -1,
      team_id: (c.team_id as number) ?? -1,
      start_frame: c.start_frame as number,
      end_frame: c.end_frame as number,
      pass_count: (c.pass_count as number) ?? 0,
      chain_xg: num(c.chain_xg) ?? null,
      start_zone: c.start_zone ?? null,
      end_zone: c.end_zone ?? null,
      box_entry: !!c.box_entry,
      start_x: num(c.start_x),
      start_y: num(c.start_y),
      end_x: num(c.end_x),
      end_y: num(c.end_y),
      attacking_direction: c.attacking_direction === 'left' || c.attacking_direction === 'right'
        ? c.attacking_direction : undefined,
      player_sequence: c.player_sequence,
    })
  }
  return out.sort((a, b) => a.start_frame - b.start_frame)
}

export interface ChainFilter {
  team?: number | null
  minPasses?: number
  boxEntriesOnly?: boolean
  /** Chains with at least this xG. */
  minXg?: number
}

/** The chains worth looking at, given what the analyst asked for. */
export function filterChains(chains: Chain[], f: ChainFilter = {}): Chain[] {
  const { team = null, minPasses = 0, boxEntriesOnly = false, minXg = 0 } = f
  return chains.filter(c =>
    (team == null || c.team_id === team)
    && c.pass_count >= minPasses
    && (!boxEntriesOnly || c.box_entry)
    && (minXg <= 0 || (c.chain_xg != null && c.chain_xg >= minXg)))
}

/** The chain containing `frame`, or null between possessions. */
export function chainAtFrame(chains: Chain[], frame: number): Chain | null {
  let lo = 0
  let hi = chains.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const c = chains[mid]
    if (c === undefined) break
    if (frame < c.start_frame) hi = mid - 1
    else if (frame > c.end_frame) lo = mid + 1
    else return c
  }
  return null
}

/** One line an analyst would actually say about a chain. */
export function describeChain(chain: Chain, fps: number): string {
  const seconds = fps > 0 ? (chain.end_frame - chain.start_frame) / fps : 0
  const parts = [`${chain.pass_count} pass${chain.pass_count === 1 ? '' : 'es'}`]
  if (seconds >= 1) parts.push(`${seconds.toFixed(0)}s`)
  if (chain.box_entry) parts.push('box entry')
  if (chain.chain_xg != null && chain.chain_xg >= 0.01) parts.push(`${chain.chain_xg.toFixed(2)} xG`)
  if (chain.end_zone) parts.push(`ends ${chain.end_zone}`)
  return parts.join(' · ')
}
